import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg } from "@/lib/api";

const SOURCE = { unsplash: "Unsplash", pexels: "Pexels", internal: "Libreria interna verificata" };

export default function StockTab({ api, projectId, initialQuery, onPick, usedUrls = [] }) {
  const [q, setQ] = useState(initialQuery || "");
  const [orientation, setOrientation] = useState("landscape");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const used = new Set(usedUrls);
  const results = res ? [...res.results].sort((a, b) => used.has(a.url) - used.has(b.url)) : [];
  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setBusy(true);
    try { const { data } = await api.get("/media/stock", { params: { q, orientation } }); setRes(data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  useEffect(() => { if (initialQuery) search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (p) => {
    onPick(p.url, p);
    api.post(`/projects/${projectId}/media/stock-use`, { photo: p }).catch(() => {});
  };
  return (
    <div data-testid="image-picker-stock">
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Es. massaggio con pietre calde, studio yoga luminoso…" className="flex-1 min-w-[200px]" data-testid="stock-search-input" />
        <Select value={orientation} onValueChange={setOrientation}>
          <SelectTrigger className="w-36" data-testid="stock-orientation-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="landscape">Orizzontale</SelectItem><SelectItem value="portrait">Verticale</SelectItem><SelectItem value="square">Quadrata</SelectItem></SelectContent>
        </Select>
        <Button type="submit" disabled={busy || !q.trim()} data-testid="stock-search-btn">{busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}<span className="ml-1.5">Cerca</span></Button>
      </form>
      {res && (
        <>
          <p className="mt-3 text-[11px] text-slate-500" data-testid="stock-source">
            Fonte: {SOURCE[res.provider]}{res.provider === "internal" ? " · aggiungi le chiavi Unsplash/Pexels in Impostazioni per cercare tra milioni di foto" : ""}
            {res.errors?.length ? ` · ${res.errors.join(" · ")}` : ""}
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="stock-results">
            {results.map((p) => (
              <button key={p.id} onClick={() => pick(p)} className="group relative rounded-lg overflow-hidden border bg-slate-100 text-left hover:border-sky-400 transition-colors" data-testid={`stock-result-${p.id}`}>
                <img src={p.thumb || p.url} alt={p.alt} loading="lazy" className={`aspect-[4/3] w-full object-cover ${used.has(p.url) ? "opacity-60" : ""}`} />
                {used.has(p.url) && <span className="absolute top-1.5 left-1.5 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-slate-700" data-testid={`stock-result-used-${p.id}`}>Già nel sito</span>}
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-5 text-[10px] text-white truncate">
                  {p.author ? `Foto di ${p.author} · ${SOURCE[p.provider]}` : p.alt}
                </span>
              </button>
            ))}
            {!res.results.length && <p className="col-span-full text-sm text-slate-500">Nessun risultato: prova con altre parole (anche in inglese).</p>}
          </div>
        </>
      )}
    </div>
  );
}
